import { ExternalLink, Mail, MapPin, Navigation, Phone } from "lucide-react";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WA_LINK, useLang } from "../i18n";
import { WhatsAppLogo } from "./WhatsAppLogo";
import { Eyebrow, Reveal } from "./Reveal";

function ContactCard({ testid, icon, iconClass, label, children }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-4 rounded-xl border border-white/5 bg-coal px-5 py-5 transition-colors hover:border-gold/25"
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconClass}`}
      >
        {icon}
      </span>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/45">
          {label}
        </div>
        <div className="mt-1 text-[15px] font-bold text-white">{children}</div>
      </div>
    </div>
  );
}

export default function Contact() {
  const { t } = useLang();
  return (
    <section id="contact" className="bg-night py-24 lg:py-28">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 lg:grid-cols-2 lg:gap-16 lg:px-10">
        <div>
          <Reveal>
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            <h2
              data-testid="contact-heading"
              className="mt-5 font-serif text-4xl text-white sm:text-5xl"
            >
              {t.contact.title}
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-4">
              <ContactCard
                testid="contact-address-card"
                icon={<MapPin className="h-5 w-5" />}
                iconClass="border border-white/10 bg-white/5 text-gold"
                label={t.contact.addressLabel}
              >
                INA-Ring 12, 91074 Herzogenaurach
              </ContactCard>

              <ContactCard
                testid="contact-phone-card"
                icon={<Phone className="h-5 w-5" />}
                iconClass="bg-callope text-white"
                label={t.contact.phoneLabel}
              >
                <a href={PHONE_TEL} className="transition-colors hover:text-gold">
                  {PHONE_DISPLAY}
                </a>
              </ContactCard>

              <ContactCard
                testid="contact-email-card"
                icon={<Mail className="h-5 w-5" />}
                iconClass="bg-gold text-black"
                label={t.contact.emailLabel}
              >
                <a href={`mailto:${EMAIL}`} className="transition-colors hover:text-gold">
                  {EMAIL}
                </a>
              </ContactCard>

              <ContactCard
                testid="contact-whatsapp-card"
                icon={<WhatsAppLogo className="h-5 w-5" />}
                iconClass="bg-wa text-white"
                label={t.contact.waLabel}
              >
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-gold"
                >
                  {PHONE_DISPLAY}
                </a>
              </ContactCard>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12} className="h-full">
          <div
            data-testid="map-embed"
            className="relative h-[420px] overflow-hidden rounded-2xl border border-white/10 lg:h-full lg:min-h-[560px]"
          >
            <iframe
              title="Singh Taxi location — INA-Ring 12, Herzogenaurach"
              src="https://maps.google.com/maps?q=INA-Ring%2012%2C%2091074%20Herzogenaurach%2C%20Germany&z=16&output=embed"
              className="h-full w-full"
              style={{
                border: 0,
                filter:
                  "grayscale(0.15) invert(0.9) contrast(0.9) hue-rotate(180deg)",
              }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute left-4 top-4 rounded-xl bg-white px-5 py-4 shadow-xl">
              <div className="text-sm font-extrabold text-[#1a1a1a]">
                INA-Ring 12
              </div>
              <div className="mt-0.5 max-w-[220px] text-xs text-gray-500">
                INA-Ring 12, 91074 Herzogenaurach, Germany
              </div>
              <div className="mt-3 flex gap-2">
                <a
                  data-testid="map-open-button"
                  href="https://www.google.com/maps/search/?api=1&query=INA-Ring+12,+91074+Herzogenaurach"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open in Google Maps"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 text-[#1a1a1a] transition-colors hover:bg-black/5"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  data-testid="map-directions-button"
                  href="https://www.google.com/maps/dir/?api=1&destination=INA-Ring+12,+91074+Herzogenaurach"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get directions"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 text-[#1a1a1a] transition-colors hover:bg-black/5"
                >
                  <Navigation className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
